import {FC, createContext, useState, useEffect, useContext, PropsWithChildren} from "react";
import {useNavigate} from "react-router-dom";
import {getStorageData, removeStorageData, AUTH_STORAGE_KEY, USER_STORAGE_KEY} from "helpers/misc";
import routes from "routes";
import {auth, profile} from "api";
import {tUpdateProfileData} from "api/types";
import {getApiError} from "helpers/getApiError";
import { eventBus } from "EventBus/EventBus";
import { identifyUser, resetUser, trackEvent, MixpanelEvents } from "mixpanel";

const useProfile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState<tUpdateProfileData | null>(null);
  const [unreadActivities, setUnreadActivities] = useState<number>(0);

  useEffect(() => {
    const token = getStorageData(AUTH_STORAGE_KEY)?.access_token;
    token ? getUser() : setUserData(null);
  }, []); // eslint-disable-line

  const getUser = () => {
    if (isLoading) return;
    setIsLoading(true);
    profile.getProfile()
      .then(({data: {data}}) => {
        setUserData(data);
        // Identify user in Mixpanel for analytics tracking
        if (data?.id) {
          identifyUser(String(data.id), {
            $email: data.email,
            $first_name: data.first_name,
            $last_name: data.last_name,
            $name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          });
        }
      })
      .catch(() => {
        setUserData(null);
        navigate('/auth/'+routes.auth.login);
      })
      .finally(() => setIsLoading(false));
  }

  const updateUserData = async (data: tUpdateProfileData, isManual?: boolean) => {
    if (isManual) {
      setUserData({...userData, ...data});
      return;
    }
    
    profile.updateProfile(data)
      .then(({data: {data}}) => {
        setUserData(data);
        eventBus.dispatch("showToast", {text: "Profile updated", type: "success"});
      })
      .catch((error) => {
        const {msg} = getApiError(error);
        eventBus.dispatch("showToast", {text: msg, type: "error"});
      });
  };

  const toggleNotificationSettings = async (
    key: "is_all_activities"|"is_plaintiff_update"|"is_defendant_update"|"is_court_update"|"is_email"|"is_push"
  ) => {
    if (!userData) return;
    const data: tUpdateProfileData = {};
    if (key === "is_all_activities") {
      const current = userData.is_plaintiff_update && userData.is_defendant_update && userData.is_court_update;
      data.is_plaintiff_update = !current;
      data.is_defendant_update = !current;
      data.is_court_update = !current;
    } else {
      data[key] = !userData[key];
    }
    return updateUserData(data);
  }

  const logout = async () => {
    try {
      // Track logout before resetting user
      trackEvent(MixpanelEvents.LOGOUT);
      await auth.logout();
      // auth
      removeStorageData(AUTH_STORAGE_KEY);
      // user
      removeStorageData(USER_STORAGE_KEY);
      // activities, cases
      setUserData(null);
      // Reset Mixpanel user identity
      resetUser();
    } catch (error) {
      const {msg} = getApiError(error);
      console.log('error', msg);
    }
  };

  return {
    userData,
    setUserData,
    isLoading,
    getUser,
    updateUserData,
    toggleNotificationSettings,
    unreadActivities, 
    setUnreadActivities,
    logout
  };
};

type tUserContext = ReturnType<typeof useProfile>;
const UserContext = createContext<tUserContext | null>(null);
UserContext.displayName = "UserContext";
export const UserContextProvider: FC<PropsWithChildren> = ({children}) => (
  <UserContext.Provider value={useProfile()}>{children}</UserContext.Provider>
);

/**
 * Provides all profile information
 * @returns (get and update profile info + logout)
 */
export const useContextProfile = () => {
  const context = useContext(UserContext);

  if (context === null) {
    throw new Error("<<< UserContext >>> must be used within a UserContextProvider");
  }

  return context as tUserContext;
};
